/**
 * smart-pack-engine.js  — phases: t1, t2
 *
 * Reads window.__emPackCatalog (server-rendered from owner lifestage collections):
 *   products[] = { handle, title, bucket:"maternity|leggings", coreName, color,
 *                  length, feature, finalSale, image, sizes:{ "M":{id,qty,avail,policy,price} } }
 *   qty = inventory_quantity (LIVE). In stock = qty > 0.
 *
 * Model: each stage/tier = CORE (the backbone + filler) + capped EXTRAS (preferred,
 * included only when in stock). Extras fill up to their cap from in-stock variety;
 * whatever they can't fill overflows into CORE. Whole-pack fallback (ok:false) only
 * if CORE can't complete the total in stock.
 *
 * Picks are distinct products (variety) and spread COLOURS (custom.product_colour;
 * blank => black) across the pack when stock allows.
 *
 * Sizing: maternity bottoms = same size; recovery = +1 (clamped at XXXL);
 * nursing/tops = snap into XS..XXL (XXS->XS, XXXL->XXL).
 *
 * Console: _testSmartPack('t2','M')
 */
(function (root) {
  'use strict';

  var PLAN = {
    t1: {
      core: ['maternity|leggings', 'maternity|shorts'],   // leggings first => leggings-biased
      extras: [],
      total: { starter: 3, essentials: 5, prepared: 7, ultimate: 9 }
    },
    t2: {
      core: ['maternity|leggings', 'maternity|shorts'],
      extras: [
        { buckets: ['postpartum|leggings', 'postpartum|shorts'], size: 'recUp',  cap: { starter: 1, essentials: 1, prepared: 1, ultimate: 2 } }, // recovery
        { buckets: ['postpartum|tops'],                           size: 'topSnap', cap: { starter: 1, essentials: 1, prepared: 2, ultimate: 2 } }  // nursing crop
      ],
      total: { starter: 3, essentials: 5, prepared: 7, ultimate: 9 }
    },
    t3: {
      core: ['maternity|leggings', 'maternity|shorts'],
      // Recovery + nursing share one budget, round-robin (even split when both in
      // stock; more of whichever's available when the other's short). Rest -> maternity.
      groups: [
        { budget: { starter: 2, essentials: 2, prepared: 3, ultimate: 4 }, members: [
          { buckets: ['postpartum|leggings', 'postpartum|shorts'], size: 'recUp' },   // recovery
          { buckets: ['postpartum|tops'],                          size: 'topSnap' }  // nursing
        ] }
      ],
      total: { starter: 3, essentials: 5, prepared: 7, ultimate: 9 }
    },
    pp: {
      // Post partum: recovery + nursing preferred (budget 4/5/6, even-flex); mum
      // BOTTOMS (shaper/vibe) fill the remainder — a sprinkle of 1/2/3 when recovery+
      // nursing are stocked, more when they're thin. No maternity, no non-nursing tops.
      core: ['mum|leggings', 'mum|shorts'],
      groups: [
        { budget: { starter: 3, essentials: 4, prepared: 5, ultimate: 6 }, members: [
          { buckets: ['postpartum|leggings', 'postpartum|shorts'], size: 'recUp' },   // recovery
          { buckets: ['postpartum|tops'],                          size: 'topSnap' }  // nursing
        ] }
      ],
      total: { starter: 3, essentials: 5, prepared: 7, ultimate: 9 }
    },
    ml: {
      // Mum uniform: mix of mum leggings + shorts + tops, variety of styles, black-based.
      preferBlack: true,
      core: [],
      groups: [
        { budget: { starter: 3, essentials: 5, prepared: 7, ultimate: 9 }, members: [
          { buckets: ['mum|leggings'], size: 'same' },
          { buckets: ['mum|shorts'],   size: 'same' },
          { buckets: ['mum|tops'],     size: 'topSnap' }
        ] }
      ],
      total: { starter: 3, essentials: 5, prepared: 7, ultimate: 9 }
    }
  };

  var SIZES = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
  function shift(sz, d) {
    var i = SIZES.indexOf(String(sz || 'M').toUpperCase()); if (i < 0) i = 3;
    i += d; if (i < 0) i = 0; if (i >= SIZES.length) i = SIZES.length - 1;
    return SIZES[i];
  }
  function snapTops(sz) {            // clamp to XS..XXL
    var i = SIZES.indexOf(String(sz || 'M').toUpperCase()); if (i < 0) i = 3;
    if (i < 1) i = 1; if (i > 6) i = 6; return SIZES[i];
  }
  // Returns a SIZE LADDER (preference order). The first size with stock wins per product.
  function sizeLadder(rule, base) {
    base = String(base || 'M').toUpperCase();
    if (rule === 'recUp') {                          // recovery: prefer +1, else current size
      var up = shift(base, 1);
      return up === base ? [base] : [up, base];
    }
    if (rule === 'topSnap') return [snapTops(base)]; // nursing/tops: XS..XXL
    return [base];                                   // maternity / mum bottoms: same
  }

  // bucket -> sizing category (matches the size-summary categories used for overrides)
  var BUCKET_CAT = {
    'maternity|leggings': 'mat-bottoms', 'maternity|shorts': 'mat-bottoms',
    'postpartum|leggings': 'rec-bottoms', 'postpartum|shorts': 'rec-bottoms',
    'postpartum|tops': 'tops', 'mum|tops': 'tops',
    'mum|leggings': 'ml-bottoms', 'mum|shorts': 'ml-bottoms'
  };

  // Lifestage moves FORWARD only: a dried bucket may borrow from the next
  // stage (maternity -> postpartum -> mum), same garment, never backwards.
  var STAGE_FORWARD = { maternity: 'postpartum', postpartum: 'mum' };
  function forwardBucket(bucket) {
    var parts = String(bucket).split('|');
    var next = STAGE_FORWARD[parts[0]];
    return next ? next + '|' + parts[1] : null;
  }
  // Size rules follow the TARGET bucket, not the slot that ran dry: a maternity
  // slot borrowing postpartum bottoms must use the recovery +1 ladder.
  function ladderForBucket(bucket, base, overrides) {
    var cat = BUCKET_CAT[bucket];
    if (cat && overrides && overrides[cat]) return [String(overrides[cat]).toUpperCase()];
    if (cat === 'rec-bottoms') return sizeLadder('recUp', base);
    if (cat === 'tops') return sizeLadder('topSnap', base);
    return sizeLadder('same', base);
  }
  // Size ladder for a set of buckets — a user override for that category forces an exact size.
  function ladderFor(rule, buckets, base, overrides) {
    var cat = BUCKET_CAT[buckets[0]];
    if (cat && overrides && overrides[cat]) return [String(overrides[cat]).toUpperCase()];
    return sizeLadder(rule, base);
  }

  // Scale a per-kit tuned table (e.g. cap {starter:1, essentials:1, prepared:2,
  // ultimate:2}) to an ARBITRARY pack total: the four tuned kits become anchor
  // points (x = the plan's DEFAULT total for that kit) and any other total
  // interpolates linearly between them — proportional shrink below the smallest
  // anchor, proportional growth above the largest. At the anchor totals the
  // result is exactly the tuned value, so today's packs are unchanged; a
  // retiered config (any totals) composes itself with the same garment ratios.
  var KIT_ORDER = ['starter', 'essentials', 'prepared', 'ultimate'];
  function scaledForTotal(table, defaults, total) {
    if (!table) return 0;
    var pts = [];
    for (var i = 0; i < KIT_ORDER.length; i++) {
      var k = KIT_ORDER[i];
      if (typeof table[k] === 'number' && typeof defaults[k] === 'number') pts.push([defaults[k], table[k]]);
    }
    if (!pts.length) return 0;
    var v;
    if (total <= pts[0][0]) v = pts[0][1] * total / pts[0][0];
    else if (total >= pts[pts.length - 1][0]) v = pts[pts.length - 1][1] * total / pts[pts.length - 1][0];
    else {
      for (var j = 1; j < pts.length; j++) {
        if (total <= pts[j][0]) {
          var a = pts[j - 1], b = pts[j];
          v = a[1] + (b[1] - a[1]) * (total - a[0]) / (b[0] - a[0]);
          break;
        }
      }
    }
    var n = Math.round(v);
    if (n < 0) n = 0;
    if (n > total) n = total;
    return n;
  }

  var _injected = null;
  function catalog() { return _injected || root.__emPackCatalog || null; }

  // Index a product's flat variants[] into { colour: { SIZE: variant } } (cached per product).
  // Colour x Size products expose every colour; legacy single-colour products have one key.
  // Falls back to the legacy `sizes` map (synth one colour) if variants[] is absent.
  function colourIndex(p) {
    if (p.__byColour) return p.__byColour;
    var by = {};
    if (p.variants && p.variants.length) {
      p.variants.forEach(function (v) {
        var c = v.color || 'Black';
        if (!by[c]) by[c] = {};
        by[c][String(v.size || '').toUpperCase()] = v;
      });
    } else if (p.sizes) {                                   // back-compat: old catalog shape
      var c2 = p.color || 'black';
      by[c2] = {};
      Object.keys(p.sizes).forEach(function (k) { by[c2][String(k).toUpperCase()] = p.sizes[k]; });
    }
    p.__byColour = by;
    return by;
  }

  // In-stock (product, colour) candidates for a bucket at a size ladder.
  // One candidate per (product, colour): the first in-stock size from the ladder.
  // In-stock gate stays qty > 0 (continue-selling at 0 is NOT auto-picked — no overselling).
  function inStockPool(bucket, ladder) {
    var cat = catalog(); if (!cat || !cat.products) return [];
    var out = [];
    cat.products.forEach(function (p) {
      // Sets are built ONLY from discount-function products (tag bundle-test →
      // bundleEligible in pack-catalog.liquid). Full-price drop items (e.g.
      // "the July 2026 drop") sit in the lifestage collections for browsing
      // but must never be auto-packed — they aren't part of the offer.
      if (p.bundleEligible === false) return;
      if (p.bucket !== bucket) return;
      var by = colourIndex(p);
      Object.keys(by).forEach(function (colour) {
        var sizes = by[colour];
        for (var s = 0; s < ladder.length; s++) {
          var v = sizes[ladder[s]];
          if (v && typeof v.qty === 'number' && v.qty > 0) { out.push({ p: p, v: v, size: ladder[s], color: colour }); break; }
        }
      });
    });
    out.sort(function (a, b) {
      if (!!a.p.finalSale !== !!b.p.finalSale) return a.p.finalSale ? 1 : -1;
      return (b.v.qty || 0) - (a.v.qty || 0);
    });
    return out;
  }

  function build(opts) {
    opts = opts || {};
    var stage = String(opts.stage || '').toLowerCase();
    var tier = opts.tier || 'essentials';
    var size = String(opts.size || 'M').toUpperCase();
    var overrides = opts.overrides || {};   // sizing-category -> exact size (user edits)
    var plan = PLAN[stage];
    var preferBlack = plan && !!plan.preferBlack;   // mum uniform: black-based, not colour-spread
    if (!plan) return { ok: false, reason: 'stage-not-enabled' };
    if (!catalog()) return { ok: false, reason: 'no-catalog' };
    // Kit totals follow the bundle config's ladder roles when the page has
    // derived them (window.__emKitTotals, set by the pack builder from
    // cart-bundle-config.json); PLAN totals are the pre-config fallback.
    var total = (root.__emKitTotals && root.__emKitTotals[tier]) || plan.total[tier];

    var picked = [], usedHandles = {}, usedColors = {}, usedVariants = {};

    // Pull one (product, colour) from a set of bucket-pools at a resolved size.
    // Pass 0 prefers a NEW style AND a new colour (variety); pass 1 takes anything
    // still unused. A (product+colour) is never picked twice, but the same style CAN
    // appear in a different colour (multi-colour spread). Colour = the variant's colour.
    function takeOne(pools) {
      for (var pass = 0; pass < 2; pass++) {
        for (var b = 0; b < pools.length; b++) {
          var pool = pools[b];
          for (var i = 0; i < pool.length; i++) {
            var cand = pool[i];
            var col = (cand.color || cand.p.color || 'black').toLowerCase();
            var vkey = cand.p.handle + '|' + col;
            if (usedVariants[vkey]) continue;                      // never same product+colour twice
            if (pass === 0) {
              if (usedHandles[cand.p.handle]) continue;            // pass 0: prefer a new style
              if (preferBlack) { if (col !== 'black') continue; }  // black-first for mum uniform
              else if (usedColors[col]) continue;                  // spread colours across the pack
            }
            usedVariants[vkey] = true; usedHandles[cand.p.handle] = true; usedColors[col] = true;
            picked.push({ bucket: cand.p.bucket, handle: cand.p.handle, title: cand.p.title,
              coreName: cand.p.coreName, length: cand.p.length, feature: cand.p.feature,
              image: (cand.v && cand.v.img) || cand.p.image, variantId: cand.v.id, size: cand.size, qty: 1,
              color: col, availableQty: cand.v.qty, policy: cand.v.policy, price: cand.v.price });
            return true;
          }
        }
      }
      return false;
    }
    // Fill up to n items, round-robin across the buckets (balanced, leggings-first bias).
    function fill(buckets, ladder, n) {
      var pools = buckets.map(function (bk) { return inStockPool(bk, ladder); });
      var got = 0, progressed = true;
      while (got < n && progressed) {
        progressed = false;
        for (var k = 0; k < pools.length && got < n; k++) {
          // round-robin one at a time keeps the garment mix balanced
          var before = picked.length;
          if (takeOne([pools[k]])) { got++; progressed = true; }
          else if (before === picked.length) { /* this bucket exhausted */ }
        }
      }
      return got;
    }

    // Round-robin across a group's members (each member has its own buckets+size),
    // up to budget n. Even split when members have stock; flexes when one is short.
    function fillGroup(members, n) {
      var memPools = members.map(function (m) {
        var lad = ladderFor(m.size, m.buckets, size, overrides);
        return m.buckets.map(function (bk) { return inStockPool(bk, lad); });
      });
      var got = 0, progressed = true;
      while (got < n && progressed) {
        progressed = false;
        for (var mi = 0; mi < memPools.length && got < n; mi++) {
          if (takeOne(memPools[mi])) { got++; progressed = true; }
        }
      }
      return got;
    }

    // 1) flexing groups (e.g. t3 recovery+nursing shared budget) — budgets
    //    scale to the ACTUAL total, so any pack size composes itself
    (plan.groups || []).forEach(function (grp) {
      var b = scaledForTotal(grp.budget, plan.total, total);
      if (b > 0) fillGroup(grp.members, b);
    });
    // 2) individually-capped extras (e.g. t2 recovery + nursing) — same scaling
    (plan.extras || []).forEach(function (ex) {
      var cap = scaledForTotal(ex.cap, plan.total, total);
      if (cap > 0) fill(ex.buckets, ladderFor(ex.size, ex.buckets, size, overrides), cap);
    });
    // 3) core fills the remainder — never fewer items
    fill(plan.core, ladderFor('same', plan.core, size, overrides), total - picked.length);

    // 4) FORWARD-LIFESTAGE fill: core buckets dry -> borrow from the next
    //    stage, same garment, target-bucket size rules. Never backwards, so
    //    a mum pack (no forward stage) skips straight to step 5.
    var fwd = (plan.core || []).slice();
    while (picked.length < total && fwd.length) {
      fwd = fwd.map(forwardBucket).filter(Boolean);
      if (!fwd.length) break;
      for (var fb = 0; fb < fwd.length && picked.length < total; fb++) {
        fill([fwd[fb]], ladderForBucket(fwd[fb], size, overrides), total - picked.length);
      }
    }

    // 5) DUPLICATES fill: variety exhausted but the tier quantity stands —
    //    bump quantity on picks with sellable depth to spare, deepest first.
    //    (Locked rule: duplicates are OK to hit the selected quantity; a
    //    whole-pack fallback only when stock genuinely can't cover it.)
    if (picked.length && picked.reduce(function (s, it) { return s + it.qty; }, 0) < total) {
      var need = total - picked.reduce(function (s, it) { return s + it.qty; }, 0);
      var guard = 200;
      while (need > 0 && guard-- > 0) {
        var best = null;
        picked.forEach(function (it) {
          var headroom = (it.availableQty || 0) - it.qty;
          if (headroom > 0 && (!best || headroom > ((best.availableQty || 0) - best.qty))) best = it;
        });
        if (!best) break;
        best.qty += 1; need -= 1;
      }
    }

    var units = picked.reduce(function (s, it) { return s + it.qty; }, 0);
    if (units < total) return { ok: false, reason: 'short', got: units, need: total };
    return { ok: true, stage: stage, tier: tier, size: size, items: picked };
  }

  function buildAll(stage, size) {
    return { starter:    build({ stage: stage, tier: 'starter',    size: size }),
             essentials: build({ stage: stage, tier: 'essentials', size: size }),
             prepared:   build({ stage: stage, tier: 'prepared',   size: size }),
             ultimate:   build({ stage: stage, tier: 'ultimate',   size: size }) };
  }

  function test(stage, size) {
    var r = buildAll(stage, size || 'M');
    ['starter', 'essentials', 'prepared', 'ultimate'].forEach(function (tier) {
      var res = r[tier];
      if (!root.console) return;
      if (!res.ok) { console.log('%c' + tier.toUpperCase() + ' -> FALLBACK (' + res.reason + (res.got != null ? ' ' + res.got + '/' + res.need : '') + ')', 'color:#c00'); return; }
      console.log('%c' + tier.toUpperCase() + ' (' + res.items.length + '):', 'font-weight:bold;color:#E8846C');
      res.items.forEach(function (it) { console.log('  ' + it.bucket + '  [' + (it.color || 'black') + ']  ' + (it.coreName || it.title) + ' [' + it.size + ']'); });
    });
    return r;
  }

  root.__emSmartPacks = { build: build, buildAll: buildAll, _setCatalog: function (c) { _injected = c; }, PLAN: PLAN };
  root._testSmartPack = test;

})(typeof window !== 'undefined' ? window : globalThis);
